import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import API from "../services/api";
import { toast } from "react-toastify";
import { CheckCircle, Download, LayoutDashboard, FileText, Calendar, Hash, BookOpen, Banknote, BadgeCheck, GraduationCap } from "lucide-react";
import { FORM_GRADE_OPTIONS, PATHWAY_OPTIONS, getOptionLabel } from "../utils/academicLevels";

const ApplicationConfirmation = () => {
  const navigate = useNavigate();
  const [application, setApplication] = useState(null);
  const [profile, setProfile]         = useState(null);
  const [visible, setVisible]         = useState(false);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    Promise.allSettled([
      API.get("/applications/latest"),
      API.get("/student/profile"),
    ]).then(([appRes, profileRes]) => {
      if (appRes.status === "fulfilled") {
        setApplication(appRes.value.data);
        setTimeout(() => setVisible(true), 80);
      } else {
        toast.error("Failed to load application details");
      }
      if (profileRes.status === "fulfilled") {
        setProfile(profileRes.value.data);
      }
    });
  }, []);

  const downloadPDF = async () => {
    try {
      setDownloading(true);
      const res  = await API.get(`/applications/${application._id}/pdf`, { responseType: "blob" });
      const url  = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href  = url;
      link.setAttribute("download", `BursaryApplication_${application.academicYear}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error(err);
      toast.error("Failed to download PDF");
    } finally {
      setDownloading(false);
    }
  };

  if (!application) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center gap-4">
        <div className="w-11 h-11 border-[3px] border-primary border-t-transparent rounded-full animate-spin" />
        <p className="text-gray-400 text-sm tracking-wide">Loading your application...</p>
      </div>
    );
  }

  const institution = profile?.institution || {};
  const progress     = application.academicProgress || {};
  const schoolLevel  = profile?.schoolLevel;

  const identifierRow = schoolLevel === "SECONDARY"
    ? { icon: <Hash size={15} />, label: "Admission No.", value: institution.admissionNumber }
    : { icon: <Hash size={15} />, label: "Registration No.", value: institution.registrationNumber };

  const progressRow = schoolLevel === "SECONDARY"
    ? {
        icon: <GraduationCap size={15} />,
        label: "Grade / Form",
        value: progress.formGrade
          ? getOptionLabel(FORM_GRADE_OPTIONS, progress.formGrade)
          : null,
      }
    : {
        icon: <GraduationCap size={15} />,
        label: "Year of Study",
        value: progress.yearOfStudy || null,
      };

  const summaryRows = [
    { icon: <Calendar size={15} />,   label: "Academic Year",  value: application.academicYear },
    { icon: <BookOpen size={15} />,   label: "Institution",    value: institution.institutionName },
    identifierRow,
    progressRow,
    ...(progress.pathway ? [{ icon: <GraduationCap size={15} />, label: "Pathway", value: getOptionLabel(PATHWAY_OPTIONS, progress.pathway) }] : []),
    { icon: <Banknote size={15} />,   label: "Total Fees",     value: application.fees?.totalFees != null ? `KES ${Number(application.fees.totalFees).toLocaleString()}` : null },
    { icon: <Banknote size={15} />,   label: "Fee Balance",    value: application.fees?.feeBalance != null ? `KES ${Number(application.fees.feeBalance).toLocaleString()}` : null },
    { icon: <BadgeCheck size={15} />, label: "Status",         value: application.status, isStatus: true },
  ];

  const fade = (delay = 0) => ({
    transition: "opacity 0.5s ease, transform 0.5s ease",
    transitionDelay: `${delay}s`,
    opacity:   visible ? 1 : 0,
    transform: visible ? "translateY(0)" : "translateY(20px)",
  });

  const statusColors = {
    SUBMITTED:    "bg-blue-50 text-blue-700 border border-blue-200",
    UNDER_REVIEW: "bg-amber-50 text-amber-700 border border-amber-200",
    APPROVED:     "bg-green-50 text-green-700 border border-green-200",
    REJECTED:     "bg-red-50 text-red-700 border border-red-200",
    DRAFT:        "bg-gray-100 text-gray-600 border border-gray-200",
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-start justify-center px-4 sm:px-6 py-10 sm:py-16">
      <div className="w-full max-w-xl space-y-4">

        <div style={fade(0)} className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 sm:p-10 text-center">
          <div
            style={{
              transition: "opacity 0.5s ease, transform 0.5s ease",
              transitionDelay: "0.15s",
              opacity:   visible ? 1 : 0,
              transform: visible ? "scale(1)" : "scale(0.6)",
            }}
            className="w-20 h-20 bg-green-50 rounded-full flex items-center justify-center mx-auto mb-6 ring-4 ring-green-100"
          >
            <CheckCircle size={38} className="text-green-500" strokeWidth={1.8} />
          </div>

          <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">
            Application Submitted!
          </h2>
          <p className="text-gray-500 text-sm leading-relaxed max-w-sm mx-auto">
            Your bursary application for{" "}
            <span className="font-semibold text-gray-700">{application.academicYear}</span>{" "}
            has been received. We'll notify you once it's reviewed.
          </p>
        </div>

        <div style={fade(0.1)} className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="flex items-center gap-3 px-5 sm:px-6 py-4 border-b border-gray-100">
            <div className="w-8 h-8 bg-blue-50 rounded-lg flex items-center justify-center flex-shrink-0">
              <FileText size={16} className="text-primary" />
            </div>
            <h3 className="font-semibold text-gray-800 text-sm">Application Summary</h3>
          </div>

          <div className="divide-y divide-gray-50 px-5 sm:px-6">
            {summaryRows.map((row, i) => (
              <div key={i} className="flex items-center justify-between py-3 gap-4">
                <div className="flex items-center gap-2.5 text-gray-400 min-w-0">
                  <span className="flex-shrink-0">{row.icon}</span>
                  <span className="text-sm text-gray-500 truncate">{row.label}</span>
                </div>
                {row.isStatus ? (
                  <span className={`text-xs font-semibold px-2.5 py-1 rounded-full flex-shrink-0 ${statusColors[row.value] || "bg-gray-100 text-gray-600"}`}>
                    {row.value || "Pending"}
                  </span>
                ) : (
                  <span className="text-sm font-medium text-gray-800 text-right truncate max-w-[55%]">
                    {row.value || <span className="text-gray-300">Not provided</span>}
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>

        <div style={fade(0.18)} className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <button
            onClick={downloadPDF}
            disabled={downloading}
            className="
              group flex items-center justify-center gap-2.5 px-5 py-3.5
              bg-primary text-white text-sm font-semibold rounded-xl
              shadow-sm
              hover:bg-blue-800 hover:shadow-md hover:-translate-y-0.5
              active:scale-[0.97] active:shadow-none
              disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:translate-y-0 disabled:hover:shadow-sm
              transition-all duration-200
            "
          >
            {downloading
              ? <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              : <Download size={16} className="group-hover:-translate-y-0.5 transition-transform duration-200" />
            }
            {downloading ? "Downloading..." : "Download PDF"}
          </button>

          <button
            onClick={() => navigate("/student-dashboard")}
            className="
              group flex items-center justify-center gap-2.5 px-5 py-3.5
              bg-white text-gray-700 text-sm font-semibold rounded-xl
              border-2 border-gray-200
              hover:border-primary hover:text-primary hover:bg-blue-50 hover:-translate-y-0.5 hover:shadow-sm
              active:scale-[0.97] active:shadow-none
              transition-all duration-200
            "
          >
            <LayoutDashboard size={16} className="group-hover:text-primary transition-colors duration-200" />
            Go to Dashboard
          </button>
        </div>

        <div style={fade(0.24)}>
          <p className="text-center text-xs text-gray-400 leading-relaxed">
            Keep a copy of this confirmation for your records.{" "}
            <br className="hidden sm:block" />
            Questions?{" "}
            <a href="/support" className="text-primary font-medium hover:underline underline-offset-2">
              Contact Bursary Support
            </a>
          </p>
        </div>

      </div>
    </div>
  );
};

export default ApplicationConfirmation;